import { useCallback, useEffect, useLayoutEffect, useRef, useState, lazy, Suspense } from 'react'
import { useExclusiveOverlay } from '../hooks/useExclusiveOverlay'
import { AnimatePresence, motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import apiClient, { extractErrorMessage } from '../api/client'
import { useAuthStore, selectUser } from '../stores/authStore'
import { useSpeech } from '../hooks/useSpeech'
import { useSwipeNavBlock } from '../hooks/useSwipeNavBlock'
import { toast } from './shared/Toast'
import { recognize } from '../services/ocr'
import { routeOcrResult } from '../services/ocrRouter'
import { prepareImageForOcr } from '../services/ocrPrepare'
import { trackEvent } from '../utils/analytics'
import { tokenizeOcrPhrase } from '../utils/ocrDisambiguation'
import type { LookupResult, LookupAddResult, ExistingCardLocation } from '../types'

// Lazy-loaded together with the Tesseract chunk: only downloaded when a mobile
// user starts a photo flow, never on desktop or at app startup.
const MobileCropModal = lazy(() => import('./MobileCropModal'))

const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform)

type Status = 'idle' | 'loading' | 'found' | 'not-found' | 'error'
type OcrPhase = 'idle' | 'downloading' | 'recognizing' | 'confirm-import' | 'empty' | 'empty-crop' | 'error' | 'disambiguate'

const OCR_MAX_FILE_BYTES = 10 * 1024 * 1024 // 10 MB — balances quality vs WASM memory pressure
const OCR_ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/gif,image/bmp,image/tiff'

/**
 * Returns true if the canvas is blank: either 0×0, fully transparent, or a uniform
 * single color (the hallmark of a Cropper that failed to load its source image).
 * Samples 5 pixels only — intentionally cheap.
 */
function isCropCanvasEmpty(canvas: HTMLCanvasElement): boolean {
  if (canvas.width === 0 || canvas.height === 0) return true
  const ctx = canvas.getContext('2d')
  if (!ctx) return true
  const { width, height } = canvas
  const points: [number, number][] = [
    [0, 0],
    [width - 1, 0],
    [0, height - 1],
    [width - 1, height - 1],
    [Math.floor(width / 2), Math.floor(height / 2)],
  ]
  const pixels = points.map(([x, y]) => ctx.getImageData(x, y, 1, 1).data)
  // All transparent → empty
  if (pixels.every(px => px[3] === 0)) return true
  // All pixels identical → uniform color (likely black from a failed image load)
  const [r0, g0, b0, a0] = pixels[0]
  return pixels.every(px => px[0] === r0 && px[1] === g0 && px[2] === b0 && px[3] === a0)
}

// If clipboard carries both an image and text (e.g. screenshot tools that also
// copy a file path), prefer the image so paste triggers OCR rather than search.
function getClipboardImage(dataTransfer: DataTransfer | null): File | null {
  if (!dataTransfer) return null
  for (const item of Array.from(dataTransfer.items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) return item.getAsFile()
  }
  return null
}

function canvasToJpegFile(canvas: HTMLCanvasElement, name: string): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob
        ? resolve(new File([blob], name, { type: 'image/jpeg' }))
        : reject(new Error('toBlob returned null')),
      'image/jpeg',
      0.92,
    )
  })
}

interface Props {
  open: boolean
  onClose: () => void
  /** 'modal' = centered desktop dialog (default); 'sheet' = bottom-sheet for mobile */
  layout?: 'modal' | 'sheet'
  /** Pre-populate the search input when the palette opens (e.g. from Share Target). */
  initialQuery?: string
  /** When set, auto-start OCR on this file as soon as the palette opens (camera FAB path). */
  initialOcrFile?: File | null
}

export default function QuickLookupPalette({ open, onClose, layout = 'modal', initialQuery, initialOcrFile }: Props) {
  // Opening the palette closes any sheet or menu that is open (one overlay at a time).
  useExclusiveOverlay(open, onClose)
  const user = useAuthStore(selectUser)
  const targetLang = user?.target_language ?? 'en'
  const { speak, isSupported: ttsSupported } = useSpeech(targetLang)
  const qc = useQueryClient()
  const navigate = useNavigate()
  // ADR-106 P3: no top-level swipe while the palette is open. The crop modal
  // it opens holds its own block, so the two nest.
  useSwipeNavBlock(open, 'quick-lookup')

  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [result, setResult] = useState<LookupResult | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [adding, setAdding] = useState(false)
  const [sheetBottom, setSheetBottom] = useState(0)

  // ── OCR state ──────────────────────────────────────────────────────────────
  const [ocrPhase, setOcrPhase] = useState<OcrPhase>('idle')
  const [ocrProgressLabel, setOcrProgressLabel] = useState('')
  const [ocrProgressFraction, setOcrProgressFraction] = useState(0)
  const [ocrImportText, setOcrImportText] = useState('')
  const [ocrErrorMsg, setOcrErrorMsg] = useState('')
  // Monotonic run ID — incrementing it "aborts" the in-flight OCR by making
  // all callbacks and post-processing no-ops for the previous run.
  const ocrRunRef = useRef(0)
  // Monotonic ID for in-flight early-downscale ops; increment on palette close
  // to prevent a completed async prep from setting cropFile after close.
  const cropPrepRunRef = useRef(0)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ── Phrase/multi-word disambiguation state ─────────────────────────────────
  const [ocrDisambiguatePhrase, setOcrDisambiguatePhrase] = useState('')
  const [ocrDisambiguateWords, setOcrDisambiguateWords] = useState<string[]>([])
  const [ocrDisambiguateSelected, setOcrDisambiguateSelected] = useState<Set<string>>(new Set())
  const [ocrPhraseLoading, setOcrPhraseLoading] = useState(false)
  const [ocrPhraseResult, setOcrPhraseResult] = useState<string | null>(null)
  const [ocrPhraseError, setOcrPhraseError] = useState<string | null>(null)
  const [ocrPhraseAdded, setOcrPhraseAdded] = useState(false)
  const [ocrWordsAdding, setOcrWordsAdding] = useState(false)
  const [ocrWordsError, setOcrWordsError] = useState<string | null>(null)

  // Mobile crop step: set when a photo arrives from a touch device.
  // Null = no crop modal shown.
  const [cropFile, setCropFile] = useState<File | null>(null)
  // Blob URL for the crop preview. Created/revoked here (not inside MobileCropModal) so the
  // URL lifetime is tied to cropFile state, not to the modal's mount/unmount cycle.
  // This eliminates a race where React batches file1→null→file2 transitions and the old URL
  // cleanup (from the modal's useEffect) races with the Cropper loading the new one.
  const [cropObjectUrl, setCropObjectUrl] = useState<string | null>(null)

  useLayoutEffect(() => {
    if (!cropFile) {
      // cropFile cleared — revoke the URL synchronously before next paint so
      // the now-unmounted Cropper can't briefly flash the previous image.
      setCropObjectUrl(prev => {
        if (prev) URL.revokeObjectURL(prev)
        return null
      })
      return
    }
    const url = URL.createObjectURL(cropFile)
    setCropObjectUrl(url)
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [cropFile])

  const isSheet = layout === 'sheet'

  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  // Mobile camera input: capture="environment" routes directly to the camera on Android.
  // Mobile gallery input: no capture, opens the OS image chooser (gallery / files).
  // Two separate elements are required — Android Chrome caches the effective capture mode
  // at input-creation time, so swapping the attribute dynamically on a single input is unreliable.
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)

  // Reset all state when palette closes; abort any in-flight OCR.
  useEffect(() => {
    if (open) {
      if (initialQuery) setQuery(initialQuery)
      setTimeout(() => inputRef.current?.focus(), 50)
    } else {
      setQuery('')
      setStatus('idle')
      setResult(null)
      setErrorMsg('')
      ocrRunRef.current++
      cropPrepRunRef.current++
      setOcrPhase('idle')
      setOcrProgressLabel('')
      setOcrProgressFraction(0)
      setOcrImportText('')
      setOcrErrorMsg('')
      setOcrDisambiguatePhrase('')
      setOcrDisambiguateWords([])
      setOcrDisambiguateSelected(new Set())
      setOcrPhraseLoading(false)
      setOcrPhraseResult(null)
      setOcrPhraseError(null)
      setOcrPhraseAdded(false)
      setOcrWordsAdding(false)
      setOcrWordsError(null)
      setCropFile(null)
    }
  }, [open]) // initialQuery intentionally omitted: applied only at open time

  // Track visual viewport to keep sheet input above soft keyboard on iOS/Android
  useEffect(() => {
    if (!isSheet || !open) { setSheetBottom(0); return }
    const vv = window.visualViewport
    if (!vv) return
    const update = () => setSheetBottom(Math.max(0, window.innerHeight - vv.height - vv.offsetTop))
    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)
    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      setSheetBottom(0)
    }
  }, [isSheet, open])

  // Debounced lookup
  useEffect(() => {
    const trimmed = query.trim()
    if (!trimmed) {
      setStatus('idle')
      setResult(null)
      setErrorMsg('')
      return
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      setStatus('loading')
      setResult(null)
      setErrorMsg('')

      try {
        const { data } = await apiClient.get<LookupResult>('/api/v1/lookup/', {
          params: { q: trimmed },
          signal: controller.signal,
        })
        if (!controller.signal.aborted) {
          setResult(data)
          setStatus(data.found ? 'found' : 'not-found')
        }
      } catch (err: unknown) {
        if (controller.signal.aborted) return
        const msg = extractErrorMessage(err, 'Lookup failed')
        setErrorMsg(msg)
        setStatus('error')
      }
    }, 300)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query])

  const handleAdd = useCallback(async () => {
    const lemma = result?.lemma ?? query.trim()
    if (!lemma || adding) return

    setAdding(true)
    try {
      const { data } = await apiClient.post<LookupAddResult>('/api/v1/lookup/add/', {
        query: lemma,
        word_entry_id: result?.word_entry_id ?? null,
      })

      const { card_id: cardId, deck_id: deckId, already_present } = data

      void qc.invalidateQueries({ queryKey: ['decks'] })
      void qc.invalidateQueries({ queryKey: ['deck', deckId] })
      void qc.invalidateQueries({ queryKey: ['cards', deckId] })

      if (already_present) {
        toast(`"${lemma}" is already in My Words`, { icon: 'ℹ️' })
      } else {
        toast(
          (t) => (
            <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span>Added &ldquo;{lemma}&rdquo; to My Words</span>
              <button
                onClick={async () => {
                  toast.dismiss(t.id)
                  try {
                    await apiClient.delete(`/api/v1/cards/${cardId}`)
                    void qc.invalidateQueries({ queryKey: ['decks'] })
                    void qc.invalidateQueries({ queryKey: ['deck', deckId] })
                    void qc.invalidateQueries({ queryKey: ['cards', deckId] })
                    toast('Undone', { icon: '↩️' })
                  } catch {
                    toast.error('Could not undo')
                  }
                }}
                style={{
                  background: 'none',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  padding: '2px 8px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-ui)',
                  fontSize: 12,
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                Undo
              </button>
            </span>
          ),
          { duration: 5000, icon: '✓' },
        )
      }

      setQuery('')
      setStatus('idle')
      setResult(null)
      setTimeout(() => inputRef.current?.focus(), 50)
    } catch (err: unknown) {
      const msg = extractErrorMessage(err, 'Could not add word')
      toast.error(msg)
    } finally {
      setAdding(false)
    }
  }, [result, query, adding])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (status === 'found' || status === 'not-found')) {
      e.preventDefault()
      handleAdd()
    }
    if (e.key === 'Escape') {
      if (ocrPhase !== 'idle') {
        cancelOcr()
      } else {
        onClose()
      }
    }
  }

  // ── OCR handlers ───────────────────────────────────────────────────────────

  const cancelOcr = useCallback(() => {
    ocrRunRef.current++
    setOcrPhase('idle')
    setOcrProgressLabel('')
    setOcrProgressFraction(0)
    setOcrImportText('')
    setOcrErrorMsg('')
    setOcrDisambiguatePhrase('')
    setOcrDisambiguateWords([])
    setOcrDisambiguateSelected(new Set())
    setOcrPhraseLoading(false)
    setOcrPhraseResult(null)
    setOcrPhraseError(null)
    setOcrPhraseAdded(false)
    setOcrWordsAdding(false)
    setOcrWordsError(null)
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [])

  const confirmTextImport = useCallback(() => {
    const text = ocrImportText
    cancelOcr()
    navigate('/text-import', { state: { prefillText: text } })
    onClose()
  }, [ocrImportText, cancelOcr, navigate, onClose])

  // ── Phrase disambiguation handlers ─────────────────────────────────────────

  const handlePhraseExplain = useCallback(async () => {
    if (ocrPhraseLoading) return
    setOcrPhraseLoading(true)
    setOcrPhraseError(null)
    setOcrPhraseResult(null)
    setOcrPhraseAdded(false)
    try {
      const { data } = await apiClient.post<{ explanation: string }>('/api/v1/ai/explain/', {
        word: ocrDisambiguatePhrase,
      })
      setOcrPhraseResult(data.explanation)
      trackEvent('ocr_phrase_defined')
    } catch (err: unknown) {
      setOcrPhraseError(extractErrorMessage(err, 'Could not get phrase explanation'))
    } finally {
      setOcrPhraseLoading(false)
    }
  }, [ocrDisambiguatePhrase, ocrPhraseLoading])

  const handlePhraseAdd = useCallback(async () => {
    if (!ocrPhraseResult || ocrPhraseAdded) return
    try {
      const { data } = await apiClient.post<LookupAddResult>('/api/v1/lookup/phrase-add/', {
        phrase: ocrDisambiguatePhrase,
        explanation: ocrPhraseResult,
      })
      void qc.invalidateQueries({ queryKey: ['decks'] })
      void qc.invalidateQueries({ queryKey: ['deck', data.deck_id] })
      void qc.invalidateQueries({ queryKey: ['cards', data.deck_id] })
      setOcrPhraseAdded(true)
      if (data.already_present) {
        toast(`"${ocrDisambiguatePhrase}" is already in My Words`, { icon: 'ℹ️' })
      } else {
        toast(`Added "${ocrDisambiguatePhrase}" to My Words`, { icon: '✓' })
      }
    } catch (err: unknown) {
      toast.error(extractErrorMessage(err, 'Could not add phrase'))
    }
  }, [ocrDisambiguatePhrase, ocrPhraseResult, ocrPhraseAdded, qc])

  const handleWordsAdd = useCallback(async () => {
    const words = Array.from(ocrDisambiguateSelected)
    if (!words.length || ocrWordsAdding) return
    setOcrWordsAdding(true)
    setOcrWordsError(null)
    trackEvent('ocr_words_picked', { count: words.length })
    try {
      await Promise.all(
        words.map(word =>
          apiClient.post<LookupAddResult>('/api/v1/lookup/add/', {
            query: word,
            word_entry_id: null,
          })
        )
      )
      void qc.invalidateQueries({ queryKey: ['decks'] })
      const plural = words.length === 1 ? 'word' : 'words'
      toast(`Added ${words.length} ${plural} to My Words`, { icon: '✓' })
      cancelOcr()
    } catch (err: unknown) {
      setOcrWordsError(extractErrorMessage(err, 'Could not add words'))
    } finally {
      setOcrWordsAdding(false)
    }
  }, [ocrDisambiguateSelected, ocrWordsAdding, qc, cancelOcr])

  // ── Mobile crop handlers ───────────────────────────────────────────────────

  const handleCropConfirm = useCallback((croppedCanvas: HTMLCanvasElement, didInteract: boolean) => {
    const originalFile = cropFile
    setCropFile(null)
    trackEvent(didInteract ? 'ocr_crop_used' : 'ocr_crop_skipped')
    // If onCropComplete hasn't fired yet the canvas will be 0×0; fall back to the full file.
    if (croppedCanvas.width === 0 || croppedCanvas.height === 0) {
      if (originalFile) void handleOcrFileRef.current(originalFile)
      return
    }
    // Defense-in-depth: if the canvas is uniformly blank (transparent or single color),
    // the Cropper image load likely failed. Show an actionable error instead of passing
    // garbage to OCR. After the URL lifecycle fix this path should never trigger, but
    // the guard catches any future regression of the same shape.
    if (isCropCanvasEmpty(croppedCanvas)) {
      trackEvent('ocr_empty_crop_blocked')
      setOcrPhase('empty-crop')
      return
    }
    void handleOcrFileRef.current(croppedCanvas)
  }, [cropFile])

  const handleCropCancel = useCallback(() => {
    setCropFile(null)
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [])

  // Downscale a captured file before the crop modal mounts, so a large camera
  // capture (12 MP+) doesn't sit in the JS heap during lazy-chunk load time.
  // "Сбой камеры — low memory" is an Android OS OOM on capture; this only
  // mitigates in-app heap pressure after the file is handed to the web layer.
  // cropPrepRunRef guards against setCropFile firing after the palette closes.
  const prepareAndSetCropFile = useCallback(async (rawFile: File) => {
    const runId = ++cropPrepRunRef.current
    try {
      const canvas = await prepareImageForOcr(rawFile)
      if (cropPrepRunRef.current !== runId) return
      const file = await canvasToJpegFile(canvas, rawFile.name || 'photo.jpg')
      if (cropPrepRunRef.current !== runId) return
      setCropFile(file)
    } catch {
      // Downscale failed (e.g. corrupted file) — fall back so the crop modal
      // still opens; OCR may fail with a size error later, which is recoverable.
      if (cropPrepRunRef.current === runId) setCropFile(rawFile)
    }
  }, [])

  const handleOcrFile = useCallback(async (input: File | HTMLCanvasElement) => {
    // Size gate only applies to raw files; a cropped canvas is already bounded.
    if (input instanceof File && input.size > OCR_MAX_FILE_BYTES) {
      trackEvent('ocr_failed', { reason: 'file_too_large' })
      setOcrPhase('error')
      setOcrErrorMsg(
        `File is too large (${(input.size / 1024 / 1024).toFixed(1)} MB). Maximum is 10 MB.`,
      )
      return
    }

    const runId = ++ocrRunRef.current
    setOcrPhase('downloading')
    setOcrProgressLabel('Loading language data…')
    setOcrProgressFraction(0)

    try {
      const prepared = await prepareImageForOcr(input)
      // PSM=7 (single text line) for crops wider than 3× their height.
      // Investigation documented in ocr.ts: results were inconclusive, but the
      // hint is preserved for narrow crops where multi-line PSM causes over-segmentation.
      const aspectRatio = prepared.width / prepared.height
      const psm = aspectRatio >= 3 ? 7 : undefined
      const ocrResult = await recognize(
        prepared,
        user?.target_language,
        ({ phase, status: s, progress }) => {
          if (ocrRunRef.current !== runId) return
          setOcrPhase(phase === 'download' ? 'downloading' : 'recognizing')
          setOcrProgressLabel(s)
          setOcrProgressFraction(progress)
        },
        psm,
      )

      if (ocrRunRef.current !== runId) return

      trackEvent('ocr_succeeded')
      const decision = routeOcrResult(ocrResult.text)

      switch (decision.kind) {
        case 'empty':
          trackEvent('ocr_failed', { reason: 'empty' })
          setOcrPhase('empty')
          break
        case 'quick-lookup': {
          trackEvent('ocr_routed_short')
          const contentWords = tokenizeOcrPhrase(decision.text)
          if (contentWords.length === 0) {
            // All tokens were stopwords — treat the same as no readable text.
            trackEvent('ocr_failed', { reason: 'empty' })
            setOcrPhase('empty')
          } else if (contentWords.length === 1) {
            // Single content word — go straight to lookup (unchanged path).
            setOcrPhase('idle')
            setQuery(contentWords[0])
            setTimeout(() => inputRef.current?.focus(), 50)
          } else {
            // Multiple content words — show disambiguation instead of a direct
            // lookup that would create a TERM-only card with no definition.
            trackEvent('ocr_disambiguation_shown')
            setOcrDisambiguatePhrase(decision.text)
            setOcrDisambiguateWords(contentWords)
            setOcrDisambiguateSelected(new Set(contentWords))
            setOcrPhraseResult(null)
            setOcrPhraseError(null)
            setOcrPhraseAdded(false)
            setOcrPhase('disambiguate')
          }
          break
        }
        case 'text-import':
          trackEvent('ocr_routed_long')
          setOcrPhase('confirm-import')
          setOcrImportText(decision.text)
          break
      }
    } catch (err: unknown) {
      if (ocrRunRef.current !== runId) return
      trackEvent('ocr_failed', { reason: 'recognition_error' })
      setOcrPhase('error')
      setOcrErrorMsg(
        err instanceof Error ? err.message : 'OCR failed. Try a clearer image or type the text directly.',
      )
    }
  }, [user?.target_language])

  // Auto-start OCR when the palette opens with a pre-supplied file (camera FAB path).
  // A ref keeps the callback stable so the effect deps stay minimal.
  const handleOcrFileRef = useRef(handleOcrFile)
  useEffect(() => { handleOcrFileRef.current = handleOcrFile }, [handleOcrFile])

  useEffect(() => {
    if (!open || !initialOcrFile) return
    if (window.matchMedia('(pointer: coarse)').matches) {
      // Mobile (camera FAB path): downscale immediately; the async decode+draw
      // takes enough time for the bottom-sheet animation to begin before cropFile
      // is set. cropPrepRunRef handles cancellation if the palette closes first.
      void prepareAndSetCropFile(initialOcrFile)
      return
    }
    // Desktop: go straight to OCR, no crop step.
    const t = setTimeout(() => handleOcrFileRef.current(initialOcrFile), 120)
    return () => clearTimeout(t)
  }, [open, initialOcrFile, prepareAndSetCropFile])

  // Clipboard-paste OCR: while the palette is open, Ctrl+V / Cmd+V with an image
  // in the clipboard starts OCR immediately — same pipeline as the file picker.
  // Mobile is excluded: clipboard images are OS-mediated there; the camera FAB is the intended path.
  useEffect(() => {
    if (!open) return
    function handlePaste(e: ClipboardEvent) {
      if (window.matchMedia('(max-width: 767px)').matches) return
      const file = getClipboardImage(e.clipboardData)
      if (!file) return
      e.preventDefault()
      trackEvent('ocr_initiated', { entry_point: 'palette_paste' })
      void handleOcrFileRef.current(file)
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [open])

  const highlightTerm = (text: string, term: string): React.ReactNode => {
    if (!term) return text
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)
    return parts.map((part, i) =>
      regex.test(part)
        ? <mark key={i} style={{ background: 'var(--accent-faint)', color: 'var(--accent)', borderRadius: 2, padding: '0 1px' }}>{part}</mark>
        : part,
    )
  }

  // ── Content assembly ───────────────────────────────────────────────────────

  // OCR content takes priority over lookup results when active.
  const ocrContent: React.ReactNode = (() => {
    if (ocrPhase === 'idle') return null
    if (ocrPhase === 'downloading' || ocrPhase === 'recognizing') {
      return (
        <OcrProgressPanel
          phase={ocrPhase}
          label={ocrProgressLabel}
          fraction={ocrProgressFraction}
          onCancel={cancelOcr}
        />
      )
    }
    if (ocrPhase === 'confirm-import') {
      return (
        <OcrImportConfirmPanel
          text={ocrImportText}
          onConfirm={confirmTextImport}
          onCancel={cancelOcr}
        />
      )
    }
    if (ocrPhase === 'empty' || ocrPhase === 'empty-crop') {
      return (
        <OcrEmptyPanel
          onRetry={() => window.matchMedia('(pointer: coarse)').matches ? galleryInputRef.current?.click() : fileInputRef.current?.click()}
          onDismiss={cancelOcr}
          emptyCrop={ocrPhase === 'empty-crop'}
        />
      )
    }
    if (ocrPhase === 'disambiguate') {
      return (
        <OcrDisambiguatePanel
          phrase={ocrDisambiguatePhrase}
          words={ocrDisambiguateWords}
          selected={ocrDisambiguateSelected}
          onToggleWord={(w) => setOcrDisambiguateSelected(prev => {
            const next = new Set(prev)
            if (next.has(w)) next.delete(w); else next.add(w)
            return next
          })}
          phraseLoading={ocrPhraseLoading}
          phraseResult={ocrPhraseResult}
          phraseError={ocrPhraseError}
          phraseAdded={ocrPhraseAdded}
          wordsAdding={ocrWordsAdding}
          wordsError={ocrWordsError}
          onDefinePhrase={handlePhraseExplain}
          onAddPhrase={handlePhraseAdd}
          onAddWords={handleWordsAdd}
          onDismiss={cancelOcr}
        />
      )
    }
    // ocrPhase === 'error'
    return (
      <OcrErrorPanel
        message={ocrErrorMsg}
        onRetry={() => { cancelOcr(); setTimeout(() => window.matchMedia('(pointer: coarse)').matches ? galleryInputRef.current?.click() : fileInputRef.current?.click(), 50) }}
        onDismiss={cancelOcr}
      />
    )
  })()

  const lookupContent: React.ReactNode = (() => {
    if (status === 'found' && result) return (
      <ResultPanel
        result={result}
        onAdd={handleAdd}
        adding={adding}
        ttsSupported={ttsSupported}
        onSpeak={() => speak(result.lemma)}
        highlightTerm={highlightTerm}
        onClose={onClose}
      />
    )
    if (status === 'not-found') return (
      <NotFoundPanel term={result?.lemma ?? query.trim()} onAdd={handleAdd} adding={adding} />
    )
    if (status === 'error') return (
      <div style={{ padding: '16px 20px', color: 'var(--danger)', fontSize: 14 }}>{errorMsg}</div>
    )
    return null
  })()

  // Photo-to-word action shown only when the palette is fully idle (no query, no OCR).
  // Mobile: two explicit actions — "Take photo" (capture) and "From gallery" (no capture).
  // Desktop: single file picker action + clipboard paste hint.
  const isMobilePointer = window.matchMedia('(pointer: coarse)').matches
  const photoAction: React.ReactNode = ocrPhase === 'idle' && !query.trim() ? (
    isMobilePointer ? (
      <MobilePhotoActions
        onCamera={() => cameraInputRef.current?.click()}
        onGallery={() => galleryInputRef.current?.click()}
      />
    ) : (
      <PhotoToWordAction
        showPasteHint={!window.matchMedia('(max-width: 767px)').matches}
        onActivate={() => fileInputRef.current?.click()}
      />
    )
  ) : null

  // Priority: OCR content > lookup content > photo action
  const resultArea = ocrContent ?? lookupContent ?? photoAction
  const hasContent = !!resultArea

  const panelMotion = isSheet
    ? {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
        transition: { type: 'spring' as const, stiffness: 350, damping: 35 },
      }
    : {
        initial: { opacity: 0, scale: 0.96, y: -8 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.96, y: -8 },
        transition: { duration: 0.15 },
      }

  const panelStyle = isSheet
    ? {
        position: 'fixed' as const,
        bottom: sheetBottom,
        left: 0,
        right: 0,
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column' as const,
        backgroundColor: 'var(--bg-surface)',
        borderRadius: '24px 24px 0 0',
        boxShadow: 'var(--shadow-float)',
        zIndex: 60,
        overflow: 'hidden',
      }
    : {
        position: 'fixed' as const,
        top: '15vh',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '100%',
        maxWidth: 560,
        backgroundColor: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        boxShadow: 'var(--shadow-float)',
        zIndex: 60,
        overflow: 'hidden',
      }

  return (
    <>
      {/* Mobile crop step: full-screen overlay, zIndex 200, above the sheet (60).
          Render only when both cropFile and cropObjectUrl are set — the URL is created
          in useLayoutEffect synchronously before the first paint, so the gap is invisible. */}
      {cropFile && cropObjectUrl && (
        <Suspense fallback={null}>
          <MobileCropModal
            imageUrl={cropObjectUrl}
            onConfirm={handleCropConfirm}
            onCancel={handleCropCancel}
          />
        </Suspense>
      )}
      <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.45)',
              zIndex: 50,
            }}
          />

          {/* Panel */}
          <motion.div
            key="panel"
            role="dialog"
            aria-label="Quick word lookup"
            aria-modal="true"
            style={panelStyle}
            {...panelMotion}
          >
            {/* Sheet drag handle */}
            {isSheet && (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 2px', flexShrink: 0 }}>
                <div
                  data-testid="sheet-drag-handle"
                  style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: 'var(--border)' }}
                />
              </div>
            )}

            {/* Hidden file input for OCR — desktop file picker (no capture) */}
            <input
              ref={fileInputRef}
              type="file"
              accept={OCR_ACCEPTED_TYPES}
              style={{ display: 'none' }}
              aria-hidden
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) {
                  trackEvent('ocr_initiated', { entry_point: 'palette' })
                  if (window.matchMedia('(pointer: coarse)').matches) {
                    void prepareAndSetCropFile(file)
                  } else {
                    void handleOcrFile(file)
                  }
                }
                e.target.value = ''
              }}
            />

            {/* Mobile camera input: capture="environment" routes directly to the camera on
                Android, bypassing the native chooser. iOS treats it as a default-source hint
                (user can still switch source in the iOS sheet). */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              aria-hidden
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) {
                  trackEvent('ocr_initiated', { entry_point: 'palette_camera' })
                  void prepareAndSetCropFile(file)
                }
                e.target.value = ''
              }}
            />

            {/* Mobile gallery input: no capture so the OS image chooser (gallery / files)
                opens instead of the camera. Kept as a separate element from cameraInputRef
                because Android Chrome caches the capture mode at input-creation time. */}
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              aria-hidden
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) {
                  trackEvent('ocr_initiated', { entry_point: 'palette_gallery' })
                  void prepareAndSetCropFile(file)
                }
                e.target.value = ''
              }}
            />

            {/* Search input row */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 16px',
              borderBottom: hasContent ? '1px solid var(--border)' : 'none',
              gap: 10,
              flexShrink: 0,
            }}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
                <circle cx="8.5" cy="8.5" r="5.75" stroke="currentColor" strokeWidth="1.75"/>
                <path d="M13 13l3.5 3.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/>
              </svg>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Look up a word…"
                aria-label="Word to look up"
                style={{
                  flex: 1,
                  padding: '16px 0',
                  background: 'none',
                  border: 'none',
                  outline: 'none',
                  fontSize: 16,
                  color: 'var(--text-primary)',
                  fontFamily: 'var(--font-ui)',
                }}
              />
              {status === 'loading' && (
                <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden className="animate-spin" style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" strokeDasharray="40 20" fill="none"/>
                </svg>
              )}
              {isSheet ? (
                <button
                  onClick={onClose}
                  aria-label="Close lookup"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    fontSize: 20,
                    lineHeight: 1,
                    padding: '8px 4px',
                    borderRadius: 8,
                    flexShrink: 0,
                    minWidth: 44,
                    minHeight: 44,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  ✕
                </button>
              ) : (
                <kbd style={{
                  fontSize: 11, padding: '2px 6px', borderRadius: 4,
                  border: '1px solid var(--border)', color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-ui)', lineHeight: 1.5, flexShrink: 0,
                }}>Esc</kbd>
              )}
            </div>

            {/* Result area — scrollable in sheet mode */}
            {isSheet ? (
              <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 'env(safe-area-inset-bottom)' }}>
                {resultArea}
              </div>
            ) : resultArea}
          </motion.div>
        </>
      )}
      </AnimatePresence>
    </>
  )
}

// ── Photo-to-word action ──────────────────────────────────────────────────────

function PhotoToWordAction({ onActivate, showPasteHint }: { onActivate: () => void; showPasteHint: boolean }) {
  return (
    <div style={{ padding: '8px 12px 12px' }}>
      <button
        onClick={onActivate}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          width: '100%',
          padding: '10px 12px',
          background: 'none',
          border: '1px solid var(--border)',
          borderRadius: 10,
          cursor: 'pointer',
          textAlign: 'left',
          color: 'var(--text-primary)',
          fontFamily: 'var(--font-ui)',
          transition: 'background-color 0.1s',
        }}
        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--bg-page)' }}
        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '' }}
        aria-label="Photo to word — open image file for OCR"
      >
        {/* Camera icon */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden
          style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
            stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round"/>
          <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="1.75"/>
        </svg>

        <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>Photo to word</span>

        {/* Beta badge */}
        <span style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: 'var(--accent)',
          background: 'var(--accent-faint)',
          padding: '2px 6px',
          borderRadius: 4,
          flexShrink: 0,
          textTransform: 'uppercase',
        }}>
          beta
        </span>
      </button>

      {showPasteHint && (
        <p
          aria-hidden
          style={{
            margin: '6px 0 0',
            fontSize: 12,
            color: 'var(--text-secondary)',
            textAlign: 'center',
            fontFamily: 'var(--font-ui)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          Or paste an image ({isMac ? '⌘V' : 'Ctrl+V'})
        </p>
      )}
    </div>
  )
}

// ── Mobile photo actions (camera + gallery) ───────────────────────────────────

function MobilePhotoActions({ onCamera, onGallery }: { onCamera: () => void; onGallery: () => void }) {
  const btnStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    padding: '10px 12px',
    background: 'none',
    border: '1px solid var(--border)',
    borderRadius: 10,
    cursor: 'pointer',
    textAlign: 'left',
    color: 'var(--text-primary)',
    fontFamily: 'var(--font-ui)',
    transition: 'background-color 0.1s',
  }
  const handleEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--bg-page)'
  }
  const handleLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    (e.currentTarget as HTMLElement).style.backgroundColor = ''
  }
  return (
    <div style={{ padding: '8px 12px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      <button onClick={onCamera} style={btnStyle} onMouseEnter={handleEnter} onMouseLeave={handleLeave}
        aria-label="Take photo for OCR">
        {/* Camera icon */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden
          style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"
            stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round"/>
          <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="1.75"/>
        </svg>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>Take photo</span>
        <span style={{
          fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
          color: 'var(--accent)', background: 'var(--accent-faint)',
          padding: '2px 6px', borderRadius: 4, flexShrink: 0, textTransform: 'uppercase',
        }}>beta</span>
      </button>
      <button onClick={onGallery} style={btnStyle} onMouseEnter={handleEnter} onMouseLeave={handleLeave}
        aria-label="Choose image from gallery for OCR">
        {/* Image/gallery icon */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden
          style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.75"/>
          <circle cx="8.5" cy="8.5" r="1.5" stroke="currentColor" strokeWidth="1.75"/>
          <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round"/>
        </svg>
        <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>From gallery</span>
      </button>
    </div>
  )
}

// ── OCR progress panel ────────────────────────────────────────────────────────

interface OcrProgressPanelProps {
  phase: 'downloading' | 'recognizing'
  label: string
  fraction: number
  onCancel: () => void
}

function OcrProgressPanel({ phase, label, fraction, onCancel }: OcrProgressPanelProps) {
  const phaseLabel = phase === 'downloading' ? 'Loading language data' : 'Reading image'
  const pct = Math.round(fraction * 100)

  return (
    <div style={{ padding: '16px 20px 18px' }}>
      {/* Phase + cancel row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          {phaseLabel}…
        </span>
        <button
          onClick={onCancel}
          style={{
            background: 'none',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '3px 10px',
            fontSize: 12,
            color: 'var(--text-secondary)',
            cursor: 'pointer',
            fontFamily: 'var(--font-ui)',
            fontWeight: 500,
          }}
        >
          Cancel
        </button>
      </div>

      {/* Progress track */}
      <div style={{
        height: 4,
        borderRadius: 2,
        backgroundColor: 'var(--border)',
        overflow: 'hidden',
        marginBottom: 8,
      }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          backgroundColor: 'var(--accent)',
          borderRadius: 2,
          transition: 'width 0.2s ease',
        }} />
      </div>

      {/* Status label + pct */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
          {label || '…'}
        </span>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>
          {pct}%
        </span>
      </div>

      {/* Beta caveat — always visible during OCR */}
      <p style={{
        margin: 0,
        fontSize: 11,
        color: 'var(--text-secondary)',
        opacity: 0.75,
        lineHeight: 1.4,
      }}>
        OCR · Beta · Lower accuracy on non-Latin scripts (Chinese, Japanese, Korean, Cyrillic)
      </p>
    </div>
  )
}

// ── OCR import confirmation panel ─────────────────────────────────────────────

interface OcrImportConfirmPanelProps {
  text: string
  onConfirm: () => void
  onCancel: () => void
}

function OcrImportConfirmPanel({ text, onConfirm, onCancel }: OcrImportConfirmPanelProps) {
  const charCount = text.length

  return (
    <div style={{ padding: '16px 20px 20px' }}>
      <p style={{ fontSize: 14, color: 'var(--text-primary)', margin: '0 0 4px', fontWeight: 500 }}>
        This looks like a longer piece of text
      </p>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 14px', lineHeight: 1.5 }}>
        {charCount} characters found. Import it to extract vocabulary instead of a single lookup?
      </p>

      {/* Preview snippet */}
      <div style={{
        fontSize: 12,
        color: 'var(--text-secondary)',
        backgroundColor: 'var(--bg-page)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '8px 12px',
        marginBottom: 16,
        maxHeight: 72,
        overflow: 'hidden',
        lineHeight: 1.5,
        fontFamily: 'var(--font-content)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}>
        {text.slice(0, 180)}{text.length > 180 ? '…' : ''}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onConfirm}
          style={{
            flex: 1,
            padding: '9px 16px',
            backgroundColor: 'var(--accent-fill)',
            color: 'var(--text-on-accent)',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-ui)',
          }}
        >
          Import text
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '9px 16px',
            backgroundColor: 'transparent',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'var(--font-ui)',
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

// ── OCR empty panel ───────────────────────────────────────────────────────────

interface OcrEmptyPanelProps {
  onRetry: () => void
  onDismiss: () => void
  /** True when OCR was blocked because the cropped canvas was blank. */
  emptyCrop?: boolean
}

function OcrEmptyPanel({ onRetry, onDismiss, emptyCrop }: OcrEmptyPanelProps) {
  const headline = emptyCrop ? 'Crop area was empty' : "Couldn't read any text"
  const body = emptyCrop
    ? 'The crop selection produced a blank image — the selection may have been too small or the image failed to load. Try a wider crop or take a new photo.'
    : 'The image may be blurry, low-contrast, or contain a script not yet supported. Try a clearer photo or type the text directly.'
  return (
    <div style={{ padding: '16px 20px 20px' }}>
      <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', margin: '0 0 4px' }}>
        {headline}
      </p>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px', lineHeight: 1.5 }}>
        {body}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onRetry}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--accent-fill)',
            color: 'var(--text-on-accent)',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-ui)',
          }}
        >
          Try another image
        </button>
        <button
          onClick={onDismiss}
          style={{
            padding: '8px 16px',
            background: 'none',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 14,
            cursor: 'pointer',
            fontFamily: 'var(--font-ui)',
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}

// ── OCR error panel ───────────────────────────────────────────────────────────

interface OcrErrorPanelProps {
  message: string
  onRetry: () => void
  onDismiss: () => void
}

function OcrErrorPanel({ message, onRetry, onDismiss }: OcrErrorPanelProps) {
  return (
    <div style={{ padding: '16px 20px 20px' }}>
      <p style={{ fontSize: 14, fontWeight: 500, color: 'var(--danger)', margin: '0 0 6px' }}>
        OCR failed
      </p>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 16px', lineHeight: 1.5 }}>
        {message}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={onRetry}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--accent-fill)',
            color: 'var(--text-on-accent)',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'var(--font-ui)',
          }}
        >
          Try another image
        </button>
        <button
          onClick={onDismiss}
          style={{
            padding: '8px 16px',
            background: 'none',
            color: 'var(--text-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            fontSize: 14,
            cursor: 'pointer',
            fontFamily: 'var(--font-ui)',
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  )
}

// ── Lookup sub-panels ─────────────────────────────────────────────────────────

interface ResultPanelProps {
  result: LookupResult
  onAdd: () => void
  adding: boolean
  ttsSupported: boolean
  onSpeak: () => void
  highlightTerm: (text: string, term: string) => React.ReactNode
  onClose: () => void
}

function ResultPanel({ result, onAdd, adding, ttsSupported, onSpeak, highlightTerm, onClose }: ResultPanelProps) {
  // Reconciliation rule (see ADR-021): when `form_of` is present, the lemma's
  // POS is implicit in the form-of label (e.g. "plural of grab" already implies
  // a noun reading). Showing a separate "Verb" pill alongside it produced the
  // contradictory-label bug reported for "grabs". Suppress POS when form_of is
  // set; keep register/variant/function — those are not contradictory.
  const metaParts: string[] = []
  if (result.pos && !result.form_of) metaParts.push(result.pos)
  if (result.register) metaParts.push(result.register)
  if (result.variant) metaParts.push(result.variant)
  if (result.function) metaParts.push(result.function)

  return (
    <div style={{ padding: '16px 20px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: metaParts.length ? 4 : 8 }}>
        <span style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-serif)' }}>
          {result.lemma}
        </span>
        {result.transcription && (
          <span style={{ fontSize: 14, color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
            /{result.transcription}/
          </span>
        )}
        {result.translation && (
          <span style={{ fontSize: 16, color: 'var(--accent)', fontWeight: 600, marginLeft: 4 }}>
            {result.translation}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {result.audio_available && ttsSupported && (
          <button
            onClick={onSpeak}
            aria-label="Play pronunciation"
            style={{
              background: 'none',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '4px 8px',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              lineHeight: 1,
              fontSize: 15,
            }}
          >
            🔊
          </button>
        )}
      </div>

      {result.form_of && (
        <p style={{ fontSize: 12, fontStyle: 'italic', color: 'var(--text-secondary)', margin: '0 0 6px', lineHeight: 1.4 }}>
          {result.form_of}
        </p>
      )}

      {metaParts.length > 0 && (
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 8px', lineHeight: 1.4 }}>
          {metaParts.join(' · ')}
          {result.collocations && (
            <span style={{ marginLeft: 8, color: 'var(--text-secondary)', opacity: 0.75 }}>
              — {result.collocations}
            </span>
          )}
        </p>
      )}

      {result.definition && (
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 8px', lineHeight: 1.55 }}>
          {result.definition}
        </p>
      )}

      {result.example && (
        <p style={{
          fontSize: 13,
          color: 'var(--text-secondary)',
          margin: '0 0 16px',
          fontStyle: 'italic',
          lineHeight: 1.5,
          borderLeft: '2px solid var(--border)',
          paddingLeft: 10,
        }}>
          {highlightTerm(result.example, result.lemma)}
        </p>
      )}

      <AddButton onAdd={onAdd} adding={adding} />
      <ExistingLocationsSection locations={result.existing_locations} onClose={onClose} />
    </div>
  )
}

interface NotFoundPanelProps {
  term: string
  onAdd: () => void
  adding: boolean
}

function NotFoundPanel({ term, onAdd, adding }: NotFoundPanelProps) {
  return (
    <div style={{ padding: '16px 20px 20px' }}>
      <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 12px' }}>
        No definition found for <strong style={{ color: 'var(--text-primary)' }}>&ldquo;{term}&rdquo;</strong>.
        Save it as a raw card?
      </p>
      <AddButton onAdd={onAdd} adding={adding} label="Save to My Words" />
    </div>
  )
}

interface AddButtonProps {
  onAdd: () => void
  adding: boolean
  label?: string
}

function AddButton({ onAdd, adding, label = 'Add to My Words' }: AddButtonProps) {
  return (
    <button
      onClick={onAdd}
      disabled={adding}
      aria-label={label}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '8px 16px',
        backgroundColor: 'var(--accent-fill)',
        color: 'var(--text-on-accent)',
        border: 'none',
        borderRadius: 8,
        fontSize: 14,
        fontWeight: 600,
        cursor: adding ? 'not-allowed' : 'pointer',
        opacity: adding ? 0.7 : 1,
        fontFamily: 'var(--font-ui)',
      }}
    >
      {adding ? 'Adding…' : label}
      {!adding && (
        <kbd style={{
          fontSize: 10, padding: '1px 5px', borderRadius: 3,
          backgroundColor: 'rgba(255,255,255,0.2)',
          fontFamily: 'var(--font-ui)',
        }}>↵</kbd>
      )}
    </button>
  )
}

// ── Existing locations ─────────────────────────────────────────────────────────

interface ExistingLocationsSectionProps {
  locations: ExistingCardLocation[]
  onClose: () => void
}

let _locationLinkNonce = 0

function ExistingLocationsSection({ locations, onClose }: ExistingLocationsSectionProps) {
  const navigate = useNavigate()
  if (!locations.length) return null

  return (
    <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
      <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 8px',
        textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Already in your decks
      </p>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {locations.map((loc) => (
          <li key={loc.deck_id}>
            <Link
              to={`/decks/${loc.deck_id}?card=${loc.card_id}`}
              onClick={(e) => {
                e.preventDefault()
                navigate(`/decks/${loc.deck_id}?card=${loc.card_id}&t=${++_locationLinkNonce}`)
                onClose()
              }}
              aria-label={`Go to deck: ${loc.deck_title}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 8px',
                borderRadius: 8,
                fontSize: 13,
                textDecoration: 'none',
                color: 'var(--text-primary)',
                backgroundColor: 'transparent',
                transition: 'background-color 0.1s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--bg-page)' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent' }}
            >
              <span aria-hidden style={{ fontSize: 14, lineHeight: 1, flexShrink: 0 }}>
                {loc.is_inbox ? '📥' : loc.is_system ? '🎓' : '🃏'}
              </span>
              <span style={{
                flex: 1,
                fontWeight: loc.is_inbox ? 600 : 400,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {loc.deck_title}
              </span>
              {loc.is_system && (
                <span style={{
                  fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 4,
                  backgroundColor: 'var(--bg-surface)', color: 'var(--text-secondary)',
                  border: '1px solid var(--border)', flexShrink: 0,
                }}>
                  system
                </span>
              )}
              {loc.last_review && (
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  studied
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── OCR disambiguation panel ──────────────────────────────────────────────────

interface OcrDisambiguatePanelProps {
  phrase: string
  words: string[]
  selected: Set<string>
  onToggleWord: (w: string) => void
  phraseLoading: boolean
  phraseResult: string | null
  phraseError: string | null
  phraseAdded: boolean
  wordsAdding: boolean
  wordsError: string | null
  onDefinePhrase: () => void
  onAddPhrase: () => void
  onAddWords: () => void
  onDismiss: () => void
}

function OcrDisambiguatePanel({
  phrase, words, selected, onToggleWord,
  phraseLoading, phraseResult, phraseError, phraseAdded,
  wordsAdding, wordsError,
  onDefinePhrase, onAddPhrase, onAddWords, onDismiss,
}: OcrDisambiguatePanelProps) {
  const selectedCount = selected.size

  return (
    <div style={{ padding: '16px 20px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <p style={{ margin: '0 0 2px', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            Multiple words detected
          </p>
          <p style={{
            margin: 0, fontSize: 12, color: 'var(--text-secondary)',
            fontFamily: 'var(--font-content)', fontStyle: 'italic',
          }}>
            &ldquo;{phrase}&rdquo;
          </p>
        </div>
        <button
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4, lineHeight: 1, fontFamily: 'var(--font-ui)' }}
        >
          ✕
        </button>
      </div>

      {/* Section A: Define as phrase/idiom via AI */}
      <div style={{ marginBottom: 16 }}>
        {!phraseResult && (
          <button
            onClick={onDefinePhrase}
            disabled={phraseLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              width: '100%', padding: '10px 14px',
              backgroundColor: 'var(--accent-fill)', color: 'var(--text-on-accent)',
              border: 'none', borderRadius: 10,
              fontSize: 14, fontWeight: 600,
              cursor: phraseLoading ? 'not-allowed' : 'pointer',
              opacity: phraseLoading ? 0.7 : 1,
              fontFamily: 'var(--font-ui)', textAlign: 'left',
            }}
          >
            <span style={{ flex: 1 }}>
              {phraseLoading ? 'Getting explanation…' : 'Define as phrase / idiom'}
            </span>
            {!phraseLoading && (
              <span style={{ fontSize: 11, opacity: 0.75, fontWeight: 500 }}>AI</span>
            )}
          </button>
        )}

        {phraseError && !phraseResult && (
          <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--danger)' }}>{phraseError}</p>
        )}

        {phraseResult && (
          <div style={{
            padding: '12px 14px',
            backgroundColor: 'var(--bg-page)', border: '1px solid var(--border)', borderRadius: 10,
          }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {phraseResult}
            </p>
            <button
              onClick={onAddPhrase}
              disabled={phraseAdded}
              style={{
                padding: '7px 14px',
                backgroundColor: phraseAdded ? 'transparent' : 'var(--accent-fill)',
                color: phraseAdded ? 'var(--text-secondary)' : 'var(--text-on-accent)',
                border: phraseAdded ? '1px solid var(--border)' : 'none',
                borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: phraseAdded ? 'default' : 'pointer',
                fontFamily: 'var(--font-ui)',
              }}
            >
              {phraseAdded ? 'Added to My Words ✓' : 'Add as card'}
            </button>
          </div>
        )}
      </div>

      {/* Divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: 'var(--text-secondary)', fontSize: 12 }}>
        <div style={{ flex: 1, height: 1, backgroundColor: 'var(--border)' }} />
        <span>or pick individual words</span>
        <div style={{ flex: 1, height: 1, backgroundColor: 'var(--border)' }} />
      </div>

      {/* Section B: Word chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
        {words.map(w => (
          <button
            key={w}
            onClick={() => onToggleWord(w)}
            style={{
              padding: '5px 12px', borderRadius: 50,
              border: `1.5px solid ${selected.has(w) ? 'var(--accent)' : 'var(--border)'}`,
              backgroundColor: selected.has(w) ? 'var(--accent-faint)' : 'transparent',
              color: selected.has(w) ? 'var(--accent)' : 'var(--text-secondary)',
              fontSize: 13, fontWeight: selected.has(w) ? 600 : 400,
              cursor: 'pointer', fontFamily: 'var(--font-ui)', transition: 'all 0.1s',
            }}
          >
            {w}
          </button>
        ))}
      </div>

      {wordsError && (
        <p style={{ margin: '0 0 8px', fontSize: 12, color: 'var(--danger)' }}>{wordsError}</p>
      )}

      <button
        onClick={onAddWords}
        disabled={selectedCount === 0 || wordsAdding}
        style={{
          padding: '8px 16px',
          backgroundColor: selectedCount === 0 ? 'transparent' : 'var(--bg-page)',
          color: selectedCount === 0 ? 'var(--text-secondary)' : 'var(--text-primary)',
          border: '1px solid var(--border)', borderRadius: 8,
          fontSize: 13, fontWeight: 500,
          cursor: selectedCount === 0 || wordsAdding ? 'not-allowed' : 'pointer',
          opacity: selectedCount === 0 || wordsAdding ? 0.5 : 1,
          fontFamily: 'var(--font-ui)',
        }}
      >
        {wordsAdding ? 'Adding…'
          : selectedCount === 0 ? 'Select words to add'
          : `Add ${selectedCount} word${selectedCount === 1 ? '' : 's'} to My Words`}
      </button>
    </div>
  )
}
